import * as Sentry from '@sentry/react';
import { ErrorPage } from '@anyroad/status-page';
import type { ReactNode } from 'react';
import type { BrowserOptions } from '@sentry/react';

export const getEnvironment = () => {
  switch (window.location.hostname) {
    case 'app.anyguide.com':
    case 'app.anyroad.com':
      return 'production';
    case 'staging.app.anyguide.com':
    case 'staging.anyroad.com':
      return 'staging';
    case 'integration.app.anyguide.com':
    case 'integration.anyroad.com':
      return 'integration';
    default:
      return 'development';
  }
};

export const LEGACY_DSN =
  'https://ee3428a32e2f434e84158c73bcec9022@o38904.ingest.sentry.io/1233249';

export const initSentry = ({
  application,
  dsn,
  options,
}: {
  application: string;
  dsn?: string;
  options?: BrowserOptions;
}) => {
  try {
    // Only initialize Sentry if we're in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.init({
        dsn: dsn ?? LEGACY_DSN,
        environment: getEnvironment(),
        integrations: [Sentry.replayIntegration()],
        tracePropagationTargets: ['localhost', /^https:\/\/.+\.anyroad\.com/u],
        initialScope: {
          tags: {
            application,
          },
        },
        normalizeDepth: 10,
        replaysOnErrorSampleRate: 0.1,
        replaysSessionSampleRate: 0.2,
        tracesSampleRate: 0.2,
        ...options,
      });
    }
  } catch (error) {
    console.warn(
      `Failed to initialize Sentry for ${application}. Most likely, Sentry was initialized elsewhere.`,
      error
    );
  }
};

const ErrorContent = ({
  error,
  componentStack,
}: {
  componentStack: string;
  error: Error;
}) => (
  <ErrorPage
    componentStack={componentStack}
    error={error}
    message="There was a problem"
  />
);
export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  if (process.env.NODE_ENV !== 'production') return children;
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <ErrorContent
          componentStack={errorData.componentStack}
          error={errorData.error as Error}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export const createSentryReduxEnhancer = () => Sentry.createReduxEnhancer();

export const withProfiler = (...args: Parameters<typeof Sentry.withProfiler>) =>
  Sentry.withProfiler(...args);
